import { useEffect, useMemo, useRef, useState } from "react";
import { X, Video, Play, Square, RotateCcw, Upload, Sparkles, Link2, Copy, Check, Trash2, ScrollText, Pause } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import ThinkingLoader from "@/components/ThinkingLoader";

const MAX_SEC = 60;

/**
 * VideoLetterModal — record a ≤60s video motivation attached to a letter.
 *
 * Phases:
 *   1. Script — Gemini drafts a talking-point script from the letter's job + body.
 *   2. Record — MediaRecorder captures webm; a timer stops at 60s.
 *   3. Preview — user can watch it back, retake, or upload.
 *   4. Uploading + Transcribing — Whisper transcription + Gemini feedback.
 *   5. Done — public share link + retention info.
 */
export default function VideoLetterModal({ letter, elevator = false, favorite = null, onClose }) {
  const { t, lang } = useT();
  const isElevator = !!elevator;
  // Effective identifier for the parent brief. Elevator-pitch mode has no
  // brief; upload/script endpoints branch on this + the `elevator` flag.
  const briefId = letter?.id || null;
  // Favorite-scoped pitch: recorded from the "Opnemen voor deze vacature"
  // button on a saved job without needing a drafted letter first.
  const favoriteId = favorite?.id || null;
  const isFavoriteScoped = !!favoriteId && !briefId && !isElevator;
  const [phase, setPhase] = useState("script"); // script | record | preview | processing | done
  const [script, setScript] = useState("");
  const [scriptBusy, setScriptBusy] = useState(true);
  const [existing, setExisting] = useState(null);

  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploaded, setUploaded] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [copied, setCopied] = useState(false);

  const videoLiveRef = useRef(null);
  const videoPlaybackRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Auto-cue teleprompter — scrolls the script inside a fixed-height box so
  // the user can read straight from the screen while recording. The scroll
  // duration matches MAX_SEC so a full script comfortably fits in one take.
  //
  // Karaoke-mode: instead of scrolling raw text, the script is split into
  // sentences and one is "active" at any time (centered, large, black),
  // with past sentences dimmed and future ones medium-grey. Advancing the
  // active index happens on RAF so it stays smooth during MediaRecorder.
  const teleprompterRef = useRef(null);
  const cueRafRef = useRef(null);
  const cueStartRef = useRef(0);
  const cueBaseRef = useRef(0);
  const activeIdxRef = useRef(0);
  const lineRefs = useRef([]);
  const [cuePlaying, setCuePlaying] = useState(false);
  const [cueSpeed, setCueSpeed] = useState(1);  // 0.75 | 1 | 1.25 | 1.5
  const [activeIdx, setActiveIdx] = useState(0);

  // Split the script into short, readable chunks — one sentence per line
  // when possible, splitting long sentences on commas so no single line
  // is longer than ~90 chars. Keeps punctuation. Empty chunks stripped.
  const sentences = useMemo(() => {
    const raw = (script || "").replace(/\s+/g, " ").trim();
    if (!raw) return [];
    // Primary split: at sentence boundaries (., !, ?) while preserving them.
    const parts = raw
      .split(/(?<=[.!?])\s+/)
      .flatMap((s) => {
        // Long sentence → break on commas (or semicolons) so each visual
        // line is quotable in one glance.
        if (s.length <= 90) return [s];
        return s.split(/(?<=[,;])\s+/).reduce((acc, chunk) => {
          if (!acc.length || (acc[acc.length - 1] + " " + chunk).length > 90) {
            acc.push(chunk);
          } else {
            acc[acc.length - 1] = acc[acc.length - 1] + " " + chunk;
          }
          return acc;
        }, []);
      })
      .map((s) => s.trim())
      .filter(Boolean);
    return parts;
  }, [script]);

  // Custom QR-label state — lets users override the default per-language
  // string ("Bekijk mijn videobrief") with their own call-to-action. Both
  // the freshly uploaded video and any pre-existing linked video can be
  // renamed from the same input field.
  const [qrLabelDraft, setQrLabelDraft] = useState("");
  const [qrLabelSaving, setQrLabelSaving] = useState(false);

  // Analytics: fetched for whichever video id is currently in scope
  // (either just-uploaded or an existing one loaded from the by-*
  // endpoint). Owner-only endpoint; the modal only shows on the
  // authenticated dashboard so we can safely call it.
  const [stats, setStats] = useState(null);
  const [statsBusy, setStatsBusy] = useState(false);
  const activeVideoId = uploaded?.id || existing?.id || null;
  const refreshStats = async () => {
    if (!activeVideoId) return;
    setStatsBusy(true);
    try {
      const r = await api.get(`/video-letters/${activeVideoId}/stats`);
      setStats(r.data);
    } catch { /* soft-fail — analytics is a nice-to-have */ }
    finally { setStatsBusy(false); }
  };
  useEffect(() => { refreshStats(); }, [activeVideoId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setQrLabelDraft((uploaded?.qr_label || existing?.qr_label || "") || "");
  }, [uploaded, existing]);
  const saveQrLabel = async () => {
    const vid = uploaded?.id || existing?.id;
    if (!vid) return;
    setQrLabelSaving(true);
    try {
      const r = await api.patch(`/video-letters/${vid}/qr-label`, { qr_label: qrLabelDraft });
      toast.success(t("vidQrLabelSaved"));
      if (uploaded) setUploaded((u) => ({ ...u, qr_label: r.data.qr_label }));
      if (existing) setExisting((e) => ({ ...e, qr_label: r.data.qr_label }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("vidQrLabelFail"));
    } finally {
      setQrLabelSaving(false);
    }
  };

  // Load existing video-letter for this brief (if any) — so the user can
  // see they already have one and choose to replace/delete.
  useEffect(() => {
    let cancel = false;
    let url = null;
    if (isElevator) url = "/video-letters/elevator-pitch";
    else if (briefId) url = `/video-letters/by-letter/${briefId}`;
    else if (favoriteId) url = `/video-letters/by-favorite/${favoriteId}`;
    if (!url) return;
    api.get(url)
      .then((r) => {
        if (cancel) return;
        // Elevator endpoint returns a single doc or null; by-letter / by-favorite return arrays.
        setExisting(Array.isArray(r.data) ? (r.data[0] || null) : (r.data || null));
      })
      .catch(() => { /* ignore */ });
    return () => { cancel = true; };
  }, [briefId, isElevator, favoriteId]);

  // Generate script on mount.
  useEffect(() => {
    if (!briefId && !isElevator && !favoriteId) return;
    let cancel = false;
    setScriptBusy(true);
    let payload;
    if (briefId) payload = { letter_id: briefId, lang: letter?.lang || lang };
    else if (favoriteId) payload = { favorite_id: favoriteId, lang: favorite?.lang || lang };
    else payload = { letter_id: null, lang: letter?.lang || lang };
    api.post("/video-letters/script", payload)
      .then((r) => { if (!cancel) setScript(r.data.script || ""); })
      .catch((e) => { if (!cancel) toast.error(e?.response?.data?.detail || t("vidScriptFail")); })
      .finally(() => { if (!cancel) setScriptBusy(false); });
    return () => { cancel = true; };
  }, [briefId, isElevator, favoriteId, letter?.lang, favorite?.lang, lang, t]);

  // Stop all media on unmount.
  useEffect(() => () => {
    stream?.getTracks?.().forEach((tr) => tr.stop());
    if (timerRef.current) clearInterval(timerRef.current);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
  }, [stream, videoUrl]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: true });
      setStream(s);
      setPhase("record");
      // Wait a tick for the ref to mount.
      setTimeout(() => {
        if (videoLiveRef.current) {
          videoLiveRef.current.srcObject = s;
          videoLiveRef.current.play?.().catch(() => {});
        }
      }, 50);
    } catch (e) {
      toast.error(t("vidCamDenied"));
    }
  };

  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    let mimeType = "video/webm;codecs=vp9,opus";
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "video/webm";
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "";
    const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
      setBlob(b);
      setVideoUrl(URL.createObjectURL(b));
      setPhase("preview");
      // Stop camera to release the LED.
      stream.getTracks().forEach((tr) => tr.stop());
      setStream(null);
    };
    mediaRecorderRef.current = rec;
    rec.start(200);
    setRecording(true);
    setElapsed(0);
    // Kick off the teleprompter in sync with the recorder so the user
    // can just read from the screen without pressing a second button.
    resetCue();
    setTimeout(() => startCue(), 100);
    timerRef.current = setInterval(() => {
      setElapsed((e) => {
        const nx = e + 1;
        if (nx >= MAX_SEC) stopRecording();
        return nx;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    pauseCue();
    setRecording(false);
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const retake = async () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl("");
    setBlob(null);
    setElapsed(0);
    await startCamera();
  };

  // Auto-cue helpers — RAF-driven so the karaoke advance stays smooth even
  // during MediaRecorder captures. Speed defaults to `1x` = MAX_SEC to
  // sweep from the first line to the last.
  const cueStep = (ts) => {
    if (!cueStartRef.current) cueStartRef.current = ts;
    const total = Math.max(1, sentences.length);
    const durationMs = (MAX_SEC * 1000) / (cueSpeed || 1);
    const elapsedMs = (ts - cueStartRef.current);
    const pct = Math.min(1, elapsedMs / durationMs);
    // Base index carried over from a paused position lets Play resume where
    // the user last was — the RAF sweep advances *from* that offset.
    const startIdx = cueBaseRef.current || 0;
    const remaining = Math.max(0, total - startIdx);
    const nextIdx = Math.min(total - 1, startIdx + Math.floor(pct * remaining));
    if (nextIdx !== activeIdxRef.current) {
      activeIdxRef.current = nextIdx;
      setActiveIdx(nextIdx);
    }
    if (pct < 1) {
      cueRafRef.current = requestAnimationFrame(cueStep);
    } else {
      cueRafRef.current = null;
      setCuePlaying(false);
    }
  };

  const startCue = () => {
    if (!sentences.length) return;
    // Resume from the current active sentence — pressing Play mid-way
    // shouldn't rewind to the beginning.
    cueBaseRef.current = activeIdxRef.current || 0;
    cueStartRef.current = 0;
    setCuePlaying(true);
    cueRafRef.current = requestAnimationFrame(cueStep);
  };
  const pauseCue = () => {
    if (cueRafRef.current) cancelAnimationFrame(cueRafRef.current);
    cueRafRef.current = null;
    setCuePlaying(false);
  };
  const resetCue = () => {
    pauseCue();
    activeIdxRef.current = 0;
    setActiveIdx(0);
    cueBaseRef.current = 0;
  };
  // Whenever the active sentence changes, scroll it into the vertical
  // centre of the teleprompter — same visual as a news autocue where
  // the current line hovers around the eyeline.
  useEffect(() => {
    const el = lineRefs.current[activeIdx];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeIdx]);
  // Clean up any lingering RAF on unmount.
  useEffect(() => () => { if (cueRafRef.current) cancelAnimationFrame(cueRafRef.current); }, []);

  const uploadAndProcess = async () => {
    if (!blob) return;
    setProcessing(true);
    setPhase("processing");
    try {
      const form = new FormData();
      form.append("file", blob, `video_letter.webm`);
      const qParams = {
        lang: letter?.lang || favorite?.lang || lang || "nl",
        duration_s: String(elapsed || 0),
      };
      if (isElevator) {
        qParams.elevator_pitch = "true";
      } else if (briefId) {
        qParams.letter_id = briefId;
      } else if (favoriteId) {
        qParams.favorite_id = favoriteId;
      }
      const q = new URLSearchParams(qParams);
      const uploadResp = await api.post(`/video-letters/upload?${q.toString()}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { id, storage_url, public_url, public_token } = uploadResp.data;
      // Kick off transcription + AI feedback.
      const tResp = await api.post(`/video-letters/${id}/transcribe`, { lang: letter?.lang || favorite?.lang || lang || "nl" });
      setUploaded({
        id,
        storage_url,
        public_url,
        public_token,
        transcript: tResp.data?.transcript || "",
        feedback: tResp.data?.feedback || null,
      });
      setPhase("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("vidUploadFail"));
      setPhase("preview");
    } finally {
      setProcessing(false);
    }
  };

  const deleteExisting = async () => {
    if (!existing?.id) return;
    if (!window.confirm(t("vidDeleteConfirm"))) return;
    try {
      await api.delete(`/video-letters/${existing.id}`);
      setExisting(null);
      toast.success(t("vidDeleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("vidDeleteFail"));
    }
  };

  const publicUrl = uploaded?.public_url
    ? `${window.location.origin}${uploaded.public_url}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const mmss = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  return (
    <div
      className={`fixed inset-0 z-[60] bg-black/70 flex ${phase === "record" ? "" : "items-center justify-center p-4"}`}
      data-testid="video-letter-modal"
      onClick={onClose}
    >
      <div
        className={`bg-[#F9F8F6] ${phase === "record"
          ? "w-full h-full max-h-none border-0 flex flex-col"
          : "border border-neutral-900 w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Video size={16} strokeWidth={1.6} className="text-amber-600" />
            <div>
              <p className="ed-eyebrow">{isElevator ? t("vidElevatorTitle") : t("vidTitle")}</p>
              {isFavoriteScoped && (favorite?.job_title || favorite?.title) && (
                <p className="font-serif text-sm text-neutral-700 mt-0.5" data-testid="video-letter-favorite-context">
                  {favorite.job_title || favorite.title}
                  {favorite.company ? ` · ${favorite.company}` : ""}
                </p>
              )}
            </div>
          </div>
          <button
            data-testid="video-letter-close"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900"
          >
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>

        <div className={phase === "record" ? "flex-1 flex flex-col p-4 min-h-0" : "p-6 space-y-5"}>
          {existing && phase === "script" && (
            <div className="ed-card p-4 flex items-start gap-3 border-amber-300 bg-amber-50/60" data-testid="video-letter-existing">
              <Sparkles size={14} strokeWidth={1.5} className="text-amber-700 mt-0.5" />
              <div className="flex-1">
                <p className="font-serif text-sm">{t("vidExistingTitle")}</p>
                <p className="text-xs text-neutral-600 mt-1">
                  {t("vidExistingBody")}{" "}
                  <a
                    href={`/video/${existing.public_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2"
                    data-testid="video-letter-existing-open"
                  >
                    {t("vidExistingOpen")}
                  </a>
                </p>
                {typeof existing.view_count === "number" && existing.view_count > 0 && (
                  <p className="text-[0.7rem] font-mono uppercase tracking-[0.12em] text-amber-800 mt-2" data-testid="video-letter-existing-views">
                    {t("vidViewCount", { n: existing.view_count })}
                  </p>
                )}
              </div>
              <button
                data-testid="video-letter-existing-delete"
                onClick={deleteExisting}
                className="p-2 border border-neutral-300 hover:border-rose-500 hover:text-rose-600"
                title={t("vidDelete")}
              >
                <Trash2 size={13} strokeWidth={1.6} />
              </button>
            </div>
          )}

          {/* Owner-side analytics — shows for existing videos before
              re-record and after upload. */}
          {existing && stats && phase === "script" && (
            <StatsPanel stats={stats} onRefresh={refreshStats} busy={statsBusy} t={t} />
          )}

          {phase === "script" && (
            <>
              <div>
                <p className="ed-eyebrow flex items-center gap-2 mb-2">
                  <Sparkles size={11} strokeWidth={1.5} className="text-amber-500" /> {t("vidScriptEyebrow", { seconds: MAX_SEC })}
                </p>
                <p className="text-sm text-neutral-600 mb-3">{t("vidScriptHint", { seconds: MAX_SEC })}</p>
                {scriptBusy ? (
                  <div className="ed-card p-6 text-center" data-testid="video-letter-script-loading">
                    <ThinkingLoader stage="letter" testId="video-letter-script-thinking" />
                  </div>
                ) : (
                  <textarea
                    data-testid="video-letter-script"
                    className="ed-input min-h-[10rem] font-serif text-base leading-relaxed"
                    value={script}
                    onChange={(e) => setScript(e.target.value)}
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs font-mono text-neutral-500">{t("vidPrivacyHint")}</p>
                <button
                  data-testid="video-letter-start-camera"
                  className="ed-btn"
                  onClick={startCamera}
                  disabled={scriptBusy || !script.trim()}
                >
                  <Video size={14} strokeWidth={1.6} /> {t("vidStartRecord")}
                </button>
              </div>
            </>
          )}

          {phase === "record" && (
            <div className="flex-1 flex flex-col gap-3 min-h-0">
              {/* Full-screen split: autocue LEFT (~60% — text is leading),
                  live video RIGHT (~40%). Fills the entire viewport height
                  minus the modal header so the karaoke stays big enough
                  to read at arm's length without squinting. */}
              <div className={`flex-1 min-h-0 grid gap-3 ${script ? "lg:grid-cols-[3fr_2fr]" : ""}`}>
                {script && (
                  <div
                    className="ed-card p-3 flex flex-col min-h-0"
                    data-testid="video-letter-teleprompter-wrap"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <p className="ed-eyebrow inline-flex items-center gap-2">
                        <ScrollText size={11} strokeWidth={1.6} /> {t("vidAutoCueEyebrow")}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {[0.75, 1, 1.25, 1.5].map((sp) => (
                          <button
                            key={sp}
                            type="button"
                            data-testid={`video-letter-cue-speed-${String(sp).replace('.', '_')}`}
                            onClick={() => setCueSpeed(sp)}
                            className={`font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 border transition-colors ${cueSpeed === sp ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
                          >
                            {sp}×
                          </button>
                        ))}
                        {!cuePlaying ? (
                          <button
                            type="button"
                            data-testid="video-letter-cue-start"
                            onClick={startCue}
                            className="ed-btn ed-btn-outline !py-1 !px-2.5 inline-flex items-center gap-1"
                            title={t("vidAutoCueStart")}
                          >
                            <Play size={11} strokeWidth={1.6} /> {t("vidAutoCueStart")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid="video-letter-cue-pause"
                            onClick={pauseCue}
                            className="ed-btn ed-btn-outline !py-1 !px-2.5 inline-flex items-center gap-1"
                            title={t("vidAutoCuePause")}
                          >
                            <Pause size={11} strokeWidth={1.6} /> {t("vidAutoCuePause")}
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid="video-letter-cue-redo"
                          onClick={resetCue}
                          className="ed-btn ed-btn-outline !py-1 !px-2.5 inline-flex items-center gap-1"
                          title={t("vidAutoCueRedo")}
                        >
                          <RotateCcw size={11} strokeWidth={1.6} /> {t("vidAutoCueRedo")}
                        </button>
                      </div>
                    </div>
                    {/* Karaoke pane — active sentence centred, dominant type,
                        past/future faded. Fills the vertical space of the
                        split so lines are readable from arm's length. */}
                    <div
                      ref={teleprompterRef}
                      className="relative flex-1 min-h-0 overflow-y-auto scroll-smooth bg-neutral-950 text-[#F9F8F6] px-8 py-6 space-y-6"
                      data-testid="video-letter-teleprompter"
                      aria-live="polite"
                    >
                      <div className="h-[45%]" aria-hidden="true" />
                      {sentences.length === 0 && (
                        <p className="text-neutral-500 italic text-2xl text-center">
                          {t("vidAutoCueEmpty") || "—"}
                        </p>
                      )}
                      {sentences.map((line, i) => {
                        const distance = i - activeIdx;
                        // Text-first design: active line is 4xl→5xl on lg,
                        // buren 3xl→4xl gedimd, verder weg 2xl fade-out.
                        let cls = "text-2xl leading-snug opacity-20";
                        if (distance === 0) cls = "text-3xl md:text-4xl lg:text-5xl leading-snug font-semibold text-white";
                        else if (Math.abs(distance) === 1) cls = "text-2xl md:text-3xl lg:text-4xl leading-snug opacity-70";
                        else if (Math.abs(distance) <= 3) cls = "text-xl md:text-2xl lg:text-3xl leading-snug opacity-40";
                        else cls = "text-xl md:text-2xl leading-snug opacity-15";
                        return (
                          <p
                            key={i}
                            ref={(el) => { lineRefs.current[i] = el; }}
                            data-testid={`video-letter-cue-line-${i}${distance === 0 ? "-active" : ""}`}
                            className={`font-serif transition-all duration-300 ${cls}`}
                          >
                            {line}
                          </p>
                        );
                      })}
                      <div className="h-[45%]" aria-hidden="true" />
                    </div>
                  </div>
                )}
                <div className="relative bg-black overflow-hidden min-h-0" data-testid="video-letter-live">
                  <video ref={videoLiveRef} muted playsInline className="w-full h-full object-cover" />
                  {recording && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2 py-1 bg-rose-600 text-white font-mono text-[0.65rem] uppercase tracking-[0.15em]">
                      <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC · {mmss(elapsed)} / {mmss(MAX_SEC)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button
                  data-testid="video-letter-cancel-record"
                  className="ed-btn ed-btn-outline"
                  onClick={() => { stream?.getTracks?.().forEach((t) => t.stop()); setStream(null); setPhase("script"); }}
                >
                  {t("vidBack")}
                </button>
                {!recording ? (
                  <button
                    data-testid="video-letter-record-start"
                    className="ed-btn bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={startRecording}
                    disabled={!stream}
                  >
                    <Play size={14} strokeWidth={1.6} /> {t("vidRecord")}
                  </button>
                ) : (
                  <button
                    data-testid="video-letter-record-stop"
                    className="ed-btn"
                    onClick={stopRecording}
                  >
                    <Square size={14} strokeWidth={1.6} /> {t("vidStop")}
                  </button>
                )}
              </div>
            </div>
          )}

          {phase === "preview" && videoUrl && (
            <div className="space-y-4">
              <div className="bg-black overflow-hidden aspect-video">
                <video
                  ref={videoPlaybackRef}
                  src={videoUrl}
                  controls
                  className="w-full h-full object-cover"
                  data-testid="video-letter-preview"
                />
              </div>
              <p className="text-xs font-mono text-neutral-500">
                {t("vidPreviewInfo", { seconds: elapsed })}
              </p>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button data-testid="video-letter-retake" className="ed-btn ed-btn-outline" onClick={retake}>
                  <RotateCcw size={14} strokeWidth={1.6} /> {t("vidRetake")}
                </button>
                <button data-testid="video-letter-upload" className="ed-btn" onClick={uploadAndProcess} disabled={processing}>
                  <Upload size={14} strokeWidth={1.6} /> {t("vidUpload")}
                </button>
              </div>
            </div>
          )}

          {phase === "processing" && (
            <div className="ed-card p-8 text-center" data-testid="video-letter-processing">
              <Sparkles size={20} strokeWidth={1.5} className="text-amber-500 mx-auto animate-pulse" />
              <p className="font-serif text-lg mt-3">{t("vidProcessing")}</p>
              <p className="font-mono text-xs text-neutral-500 mt-2">{t("vidProcessingHint")}</p>
              <div className="mt-3 flex justify-center">
                <ThinkingLoader stage="letter" testId="video-letter-processing-thinking" />
              </div>
            </div>
          )}

          {phase === "done" && uploaded && (
            <div className="space-y-4" data-testid="video-letter-done">
              <div className="bg-black overflow-hidden aspect-video">
                <video src={uploaded.storage_url} controls className="w-full h-full object-cover" />
              </div>

              <div className="ed-card p-4 space-y-2 border-emerald-300 bg-emerald-50/60">
                <p className="ed-eyebrow inline-flex items-center gap-2">
                  <Link2 size={11} strokeWidth={1.5} /> {t("vidShareEyebrow")}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="video-letter-share-url"
                    readOnly
                    className="ed-input flex-1 font-mono text-xs"
                    value={publicUrl}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    data-testid="video-letter-copy-share"
                    onClick={copyLink}
                    className="ed-btn ed-btn-outline"
                    title={t("vidCopy")}
                  >
                    {copied ? <><Check size={13} strokeWidth={1.6} /> {t("vidCopied")}</> : <><Copy size={13} strokeWidth={1.6} /> {t("vidCopy")}</>}
                  </button>
                </div>
                <p className="text-xs text-neutral-500">{t("vidShareHint")}</p>
              </div>

              <div className="ed-card p-4 space-y-1 border-amber-300 bg-amber-50/60" data-testid="video-letter-qr-hint">
                <p className="ed-eyebrow inline-flex items-center gap-2">
                  <Sparkles size={11} strokeWidth={1.5} className="text-amber-600" /> {t("vidQrEyebrow")}
                </p>
                <p className="text-xs text-neutral-600 leading-relaxed">{t("vidQrHint")}</p>
                <div className="pt-2 space-y-1">
                  <p className="ed-label">{t("vidQrLabelTitle")}</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      data-testid="video-letter-qr-label-input"
                      className="ed-input flex-1 font-mono text-xs"
                      placeholder={t("vidQrLabelPlaceholder")}
                      value={qrLabelDraft}
                      onChange={(e) => setQrLabelDraft(e.target.value.slice(0, 60))}
                      maxLength={60}
                    />
                    <button
                      type="button"
                      data-testid="video-letter-qr-label-save"
                      onClick={saveQrLabel}
                      disabled={qrLabelSaving || (qrLabelDraft || "") === (uploaded?.qr_label || "")}
                      className="ed-btn ed-btn-outline !py-1.5"
                    >
                      {qrLabelSaving ? t("vidQrLabelSaving") : t("vidQrLabelSave")}
                    </button>
                  </div>
                </div>
              </div>

              {uploaded.feedback && (
                <div className="ed-card p-4 space-y-3" data-testid="video-letter-feedback">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-2xl">{uploaded.feedback.match_score ?? "—"}%</span>
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">{t("vidMatchScore")}</span>
                    <span className="ml-auto font-mono text-xs text-neutral-500">{t("vidClarity")}: {uploaded.feedback.clarity_score ?? "—"}%</span>
                  </div>
                  {uploaded.feedback.summary && (
                    <p className="font-serif text-sm italic text-neutral-700">{uploaded.feedback.summary}</p>
                  )}
                  {Array.isArray(uploaded.feedback.strengths) && uploaded.feedback.strengths.length > 0 && (
                    <div>
                      <p className="ed-eyebrow mb-1">{t("vidStrengths")}</p>
                      <ul className="text-sm list-disc pl-5 space-y-1">
                        {uploaded.feedback.strengths.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(uploaded.feedback.improvements) && uploaded.feedback.improvements.length > 0 && (
                    <div>
                      <p className="ed-eyebrow mb-1">{t("vidTips")}</p>
                      <ul className="text-sm list-disc pl-5 space-y-1">
                        {uploaded.feedback.improvements.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {uploaded.transcript && (
                <details className="ed-card p-4" data-testid="video-letter-transcript">
                  <summary className="ed-eyebrow cursor-pointer">{t("vidTranscript")}</summary>
                  <p className="text-sm text-neutral-700 mt-3 leading-relaxed whitespace-pre-line">{uploaded.transcript}</p>
                </details>
              )}

              {stats && (
                <StatsPanel stats={stats} onRefresh={refreshStats} busy={statsBusy} t={t} />
              )}

              <div className="flex justify-end">
                <button className="ed-btn" onClick={onClose} data-testid="video-letter-close-done">
                  {t("vidDone")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * StatsPanel — owner-only viewer analytics for a video pitch.
 *
 * Shows total views, distinct sessions, and a ranked list of identified
 * viewers by max watch time. Anonymous sessions are counted separately so
 * the list stays clean — we never surface raw viewer_id hashes.
 */
function StatsPanel({ stats, onRefresh, busy, t }) {
  const dur = Number(stats?.duration_s || 0);
  const rows = stats?.identified_viewers || [];
  const totalViews = stats?.view_count || 0;
  const anon = stats?.anonymous_sessions || 0;
  const pct = (s) => (dur > 0 ? Math.min(100, Math.round((s / dur) * 100)) : null);
  return (
    <div className="ed-card p-4 space-y-3" data-testid="video-letter-stats">
      <div className="flex items-center justify-between gap-2">
        <p className="ed-eyebrow inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
          {t("vidStatsTitle")}
        </p>
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          data-testid="video-letter-stats-refresh"
          className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 underline decoration-dotted"
        >
          {busy ? t("vidStatsRefreshing") : t("vidStatsRefresh")}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="border border-neutral-200 py-2">
          <p className="font-serif text-2xl" data-testid="video-letter-stats-views">{totalViews}</p>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">{t("vidStatsViews")}</p>
        </div>
        <div className="border border-neutral-200 py-2">
          <p className="font-serif text-2xl" data-testid="video-letter-stats-known">{rows.length}</p>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">{t("vidStatsIdentified")}</p>
        </div>
        <div className="border border-neutral-200 py-2">
          <p className="font-serif text-2xl" data-testid="video-letter-stats-anon">{anon}</p>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">{t("vidStatsAnon")}</p>
        </div>
      </div>
      {rows.length > 0 ? (
        <div className="space-y-1.5" data-testid="video-letter-stats-list">
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t("vidStatsTop")}</p>
          {rows.slice(0, 5).map((row, i) => {
            const p = pct(row.max_watched_s);
            return (
              <div
                key={i}
                data-testid={`video-letter-stats-row-${i}`}
                className={`border-l-2 pl-3 py-1.5 ${i === 0 ? "border-l-amber-500 bg-amber-50/60" : "border-l-neutral-300"}`}
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <p className="font-serif text-sm">
                    {row.viewer_name || row.viewer_company || t("vidStatsAnonRow")}
                    {row.viewer_name && row.viewer_company && (
                      <span className="text-neutral-500 text-xs"> · {row.viewer_company}</span>
                    )}
                  </p>
                  <span className="font-mono text-[0.65rem] tabular-nums text-neutral-700">
                    {Math.round(row.max_watched_s)}s{p !== null ? ` · ${p}%` : ""}
                    {row.completions > 0 ? ` · ${row.completions}×` : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-neutral-500" data-testid="video-letter-stats-empty">
          {totalViews > 0 ? t("vidStatsAnonOnly") : t("vidStatsEmpty")}
        </p>
      )}
    </div>
  );
}
